import { type FormEvent, useCallback, useEffect, useState } from "react";
import { type Api, ApiError, type SiteRedirect } from "../api";

/**
 * Redirects (SEO 301-preservation): old paths that should forward to a new
 * home instead of 404ing. Renamed blog posts get one automatically; this
 * card covers everything else (old-site URLs, campaign paths). Rules apply
 * only when no page exists at the path — a real page always wins.
 */
export function RedirectsCard({ api }: { api: Api }) {
  const [redirects, setRedirects] = useState<SiteRedirect[] | null>(null);
  const [sourcePath, setSourcePath] = useState("");
  const [targetUrl, setTargetUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { redirects: rows } = await api.listRedirects();
      setRedirects(rows);
    } catch {
      // leave loading state; a refresh retries
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const add = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const source = sourcePath.trim();
    const target = targetUrl.trim();
    if (!source || !target || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { redirects: rows } = await api.createRedirect({
        sourcePath: source.startsWith("/") ? source : `/${source}`,
        targetUrl: target,
      });
      setRedirects(rows);
      setSourcePath("");
      setTargetUrl("");
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Could not add the redirect.",
      );
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteRedirect(id);
      setRedirects(
        (current) => current?.filter((row) => row.id !== id) ?? null,
      );
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Could not remove the redirect.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card" data-testid="redirects-card">
      <div className="card-head">
        <h2>Redirects</h2>
        <p className="muted">
          Forward old links to their new home instead of a &ldquo;page not
          found&rdquo; — renamed blog posts get one automatically.
        </p>
      </div>
      <form className="redirects-form" onSubmit={add}>
        <input
          aria-label="Old path"
          disabled={busy}
          onChange={(e) => setSourcePath(e.target.value)}
          placeholder="/old-page"
          value={sourcePath}
        />
        <input
          aria-label="Redirect to"
          disabled={busy}
          onChange={(e) => setTargetUrl(e.target.value)}
          placeholder="/new-page or https://…"
          value={targetUrl}
        />
        <button
          className="btn-secondary"
          disabled={busy || !sourcePath.trim() || !targetUrl.trim()}
          type="submit"
        >
          Add
        </button>
      </form>
      {error && <p className="error small">{error}</p>}
      {redirects === null ? (
        <p className="muted small">Loading…</p>
      ) : redirects.length === 0 ? (
        <p className="muted small">No redirects yet.</p>
      ) : (
        <ul className="redirects-list">
          {redirects.map((row) => (
            <li key={row.id}>
              <code>{row.sourcePath}</code>
              <span aria-hidden="true">→</span>
              <code>{row.targetUrl}</code>
              <span className="muted small">{row.statusCode}</span>
              <button
                aria-label={`Remove redirect ${row.sourcePath}`}
                className="btn-secondary"
                disabled={busy}
                onClick={() => remove(row.id)}
                type="button"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
